/**
 * Iter66 — /admin/ingesta-jobs
 * Tabla de todos los jobs de ingesta histórica con polling 5 s + reintentar.
 */
import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Database, RefreshCw, RotateCcw, Loader2, Upload as UploadIcon, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { req } from '../services/api';

const ESTADO_COLOR = {
  encolado: 'bg-ink-100 text-ink-700 border-ink-200',
  descargando: 'bg-blue-50 text-blue-700 border-blue-200',
  procesando: 'bg-amber-50 text-amber-700 border-amber-200',
  interrumpido: 'bg-orange-50 text-orange-700 border-orange-200',
  completado: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  error: 'bg-red-50 text-red-700 border-red-200',
};

const fmtMB = (b) => (b ? `${(b / (1024 * 1024)).toFixed(1)} MB` : '—');
const fmtFecha = (s) => (s ? String(s).slice(0, 19).replace('T', ' ') : '—');

export default function IngestaJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);
  const [filtro, setFiltro] = useState('all');

  const cargar = async () => {
    try {
      const data = await req('/api/prospeccion/ingesta-jobs');
      setJobs(data || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, 5000);
    return () => clearInterval(t);
  }, []);

  const reintentar = async (job_id) => {
    setRetrying(job_id);
    try {
      await req(`/api/prospeccion/ingesta-jobs/${job_id}/retry`, { method: 'POST', body: JSON.stringify({}) });
      toast.success('Job reencolado');
      cargar();
    } catch (e) {
      toast.error(e.message);
    } finally { setRetrying(null); }
  };

  const filtered = filtro === 'all' ? jobs : jobs.filter((j) => j.estado === filtro);
  const totales = jobs.reduce((acc, j) => {
    acc[j.estado] = (acc[j.estado] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-5" data-testid="page-ingesta-jobs">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-ink-900 flex items-center gap-2">
            <Database className="w-7 h-7 text-brand-500" /> Ingesta jobs
          </h1>
          <p className="text-sm text-ink-500 mt-1">
            Histórico de descargas/uploads de ZIPs CODICE 2.07. Polling cada 5 segundos.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={cargar} data-testid="btn-ingesta-refresh">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Actualizar
        </Button>
      </div>

      {/* Filtros */}
      <Card className="p-4 border-ink-200 bg-white">
        <div className="flex flex-wrap gap-2">
          {['all', 'encolado', 'descargando', 'procesando', 'interrumpido', 'completado', 'error'].map((e) => (
            <button
              key={e}
              onClick={() => setFiltro(e)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium border transition ${
                filtro === e
                  ? 'bg-brand-500 text-white border-brand-500'
                  : 'bg-white text-ink-700 border-ink-200 hover:bg-ink-50'
              }`}
              data-testid={`filtro-ingesta-${e}`}
            >
              {e === 'all' ? 'Todos' : e}
              {e !== 'all' && totales[e] != null && <span className="ml-1.5 opacity-70">({totales[e]})</span>}
            </button>
          ))}
        </div>
      </Card>

      {loading ? (
        <div className="py-12 text-center text-ink-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Cargando…
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center border-ink-200 text-ink-400">
          {filtro === 'all' ? 'No hay jobs aún. Lanza una importación desde /prospeccion/historico.' : `No hay jobs en estado "${filtro}".`}
        </Card>
      ) : (
        <Card className="border-ink-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="ingesta-tabla">
            <thead>
              <tr className="text-left text-xs text-ink-500 border-b border-ink-100">
                <th className="px-3 py-2.5">Estado</th>
                <th className="px-3 py-2.5">Año</th>
                <th className="px-3 py-2.5">Origen</th>
                <th className="px-3 py-2.5 text-right">Bytes</th>
                <th className="px-3 py-2.5 text-right">Atoms</th>
                <th className="px-3 py-2.5 text-right">Agua</th>
                <th className="px-3 py-2.5 text-right">Gestoras</th>
                <th className="px-3 py-2.5">Creado</th>
                <th className="px-3 py-2.5">Actualizado</th>
                <th className="px-3 py-2.5">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((j) => {
                const p = j.progreso || {};
                const pctBytes = p.bytes_total > 0 ? Math.round((p.bytes_descargados / p.bytes_total) * 100) : null;
                const pctAtoms = p.atoms_total > 0 ? Math.round((p.atoms_procesados / p.atoms_total) * 100) : null;
                const reintentable = j.estado === 'error' || j.estado === 'interrumpido';
                const reintentablePorOrigen = j.origen !== 'upload';
                return (
                  <tr key={j.job_id} className="border-b border-ink-100 hover:bg-ink-50" data-testid={`ingesta-fila-${j.job_id}`}>
                    <td className="px-3 py-2.5">
                      <Badge className={`${ESTADO_COLOR[j.estado] || 'bg-ink-100'} border`}>
                        {j.estado}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5 font-mono">{j.año}</td>
                    <td className="px-3 py-2.5">
                      {j.origen === 'upload' ? (
                        <span className="inline-flex items-center gap-1 text-xs text-violet-700">
                          <UploadIcon className="w-3 h-3" /> upload
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-blue-700">
                          <Globe className="w-3 h-3" /> url
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      <div>{fmtMB(p.bytes_descargados)}{p.bytes_total > 0 && <span className="text-ink-400"> / {fmtMB(p.bytes_total)}</span>}</div>
                      {pctBytes != null && <div className="text-[11px] text-ink-400">{pctBytes}%</div>}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      <div>{p.atoms_procesados || 0}{p.atoms_total > 0 && <span className="text-ink-400"> / {p.atoms_total}</span>}</div>
                      {pctAtoms != null && <div className="text-[11px] text-ink-400">{pctAtoms}%</div>}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{p.agua_encontrados || 0}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{p.gestoras_creadas || 0}</td>
                    <td className="px-3 py-2.5 text-xs text-ink-500">{fmtFecha(j.creado)}</td>
                    <td className="px-3 py-2.5 text-xs text-ink-500">{fmtFecha(j.actualizado)}</td>
                    <td className="px-3 py-2.5">
                      {reintentable && reintentablePorOrigen ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => reintentar(j.job_id)}
                          disabled={retrying === j.job_id}
                          data-testid={`btn-retry-${j.job_id}`}
                        >
                          {retrying === j.job_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                          Reintentar
                        </Button>
                      ) : reintentable && !reintentablePorOrigen ? (
                        <span className="text-[11px] text-ink-400">re-subir ZIP</span>
                      ) : (
                        <span className="text-[11px] text-ink-300">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* Errores destacados */}
      {filtered.filter((j) => j.error_msg).length > 0 && (
        <Card className="p-4 border-red-200 bg-red-50/50" data-testid="ingesta-errores">
          <h3 className="font-heading font-semibold text-red-700 mb-2 text-sm">Mensajes de error</h3>
          <ul className="text-xs space-y-1 text-red-700">
            {filtered.filter((j) => j.error_msg).slice(0, 10).map((j) => (
              <li key={j.job_id}>
                <span className="font-mono">{j.año}</span> · <span className="font-mono text-red-500">{j.job_id.slice(0, 8)}…</span> — {j.error_msg}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
